// R115 calendar hub — the payload normalisers, moved verbatim (pure) out of
// StaysCalendarPage.ts. Every R111 / R112 field is optional — an older server
// (or a partial deploy) must still draw the R110 calendar. R115: the window
// may be 7..120 columns (B9's lazy past + 8 weeks), never clamped to 14|28|56
// (a back window used to be drawn at the client's own day count).
import type { ArrivalCheck, Block, CalData, CheckItem, Detail, EciLco, EciLcoSide, FilterCounts, ItemState, Kpis, Opp, Property, Stay, TurnEv, WoEv } from './types';
import { addDays, validDate } from '../../../../lib/calendarCore.mjs';

export const DONE_STATUSES = ['cleaned', 'verified', 'ready'];
export const ITEM_STATES: readonly ItemState[] = ['pending', 'done', 'waived', 'na', 'failed'];

/** Initials for discs and avatars ("Maria Lopez" → "ML"; a single word → its first two letters). */
export function initials(name: string): string {
  const words = String(name || '').split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w)).map(w => w.replace(/[^A-Za-z0-9]/g, ''));
  if (!words.length) return '?';
  const a = words[0][0] || '';
  const b = words.length > 1 ? (words[words.length - 1][0] || '') : (words[0][1] || '');
  return (a + b).toUpperCase();
}

/** Today in the business timezone — only a fallback; the server's as_of is the truth whenever a payload is loaded. */
export function bizToday(): string {
  try { const v = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }); if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; } catch { /* old engine */ }
  return new Date().toISOString().slice(0, 10);
}

/** The window the server answered, plus R115's B9 extras (anchor = today − 1 when the client asked with ?back). */
export interface CalWindowInfo { anchor: string | null; back: number }

/** One turn event (list, detail or popover). R112 extras default from the R111 fields when the server predates them. */
export function normTurn(t: any, today: string): TurnEv | null {
  if (!t || t.id == null || t.listing_id == null || !validDate(t.date)) return null;
  const status = String(t.status || '');
  const assignees = (Array.isArray(t.assignees) ? t.assignees : []).map(String);
  const done = typeof t.done === 'boolean' ? t.done : DONE_STATUSES.includes(status.toLowerCase());
  const same_day = t.same_day === true;
  return {
    ...t, id: String(t.id), listing_id: String(t.listing_id), status, assignees, same_day,
    unassigned: t.unassigned === true, next_check_in: validDate(t.next_check_in) ? t.next_check_in : null,
    assignee_ids: (Array.isArray(t.assignee_ids) ? t.assignee_ids : []).map(String),
    initials: Array.isArray(t.initials) && t.initials.length ? t.initials.map(String) : assignees.map((n: string) => initials(n)),
    photos: Math.max(0, Number(t.photos) || 0),
    checkout_guest: String(t.checkout_guest || '').slice(0, 120),
    moved_from: validDate(t.moved_from) ? t.moved_from : null,
    origin_key: t.origin_key ? String(t.origin_key) : null,
    gap_days: Number.isFinite(Number(t.gap_days)) && t.gap_days != null ? Number(t.gap_days) : null,
    done,
    at_risk: typeof t.at_risk === 'boolean' ? t.at_risk : (same_day && t.date <= today && !done),
  };
}

/** The list payload. `fallbackDays` = the columns the client asked for (used when the answer's own days is unusable). */
export function normalizeCal(j: any, fallbackDays: number): CalData & CalWindowInfo {
  const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? v as T[] : []);
  const obj = (v: unknown): Record<string, any> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, any> : {});
  const d = obj(j) as CalData & CalWindowInfo;
  if (!validDate(d.from)) d.from = addDays(d.as_of && validDate(d.as_of) ? d.as_of : bizToday(), -1);
  const nd = Math.round(Number(d.days));
  d.days = Number.isFinite(nd) && nd >= 7 && nd <= 120 ? nd : fallbackDays;
  if (!validDate(d.as_of)) d.as_of = addDays(d.from, 1);
  if (!validDate(d.to) || d.to !== addDays(d.from, d.days - 1)) d.to = addDays(d.from, d.days - 1);
  d.anchor = validDate((j || {}).anchor) ? (j || {}).anchor : null;
  d.back = Math.max(0, Math.round(Number((j || {}).back)) || 0);
  d.properties = arr<Property>(d.properties).filter(p => p && p.listing_id != null).map(p => ({
    ...p, listing_id: String(p.listing_id), name: String(p.name || p.listing_id),
    turn_dates: arr<string>(p.turn_dates).filter(x => validDate(x)).sort(),
    turn_next: p.turn_next && typeof p.turn_next === 'object' && validDate(p.turn_next.date) ? { date: p.turn_next.date, status: String(p.turn_next.status || ''), unassigned: p.turn_next.unassigned === true } : null,
  }));
  d.stays = arr<Stay>(d.stays).filter(s => s && s.id != null && s.check_in && s.check_out).map(s => ({
    ...s, id: String(s.id), listing_id: String(s.listing_id), guest_name: String(s.guest_name || ''), nights: Number(s.nights) || 1,
    opportunities: arr<Opp>(s.opportunities), flagged: s.flagged && typeof s.flagged === 'object' ? s.flagged : null, turnover: s.turnover && typeof s.turnover === 'object' ? s.turnover : null,
    negative: s.negative === true, long_stay: s.long_stay === true, review: s.review && typeof s.review === 'object' ? s.review : null, checks: s.checks && typeof s.checks === 'object' && s.checks.rollup ? s.checks : null,
    impossible: s.kind === 'inquiry' && s.impossible && typeof s.impossible === 'object' && s.impossible.why ? { why: String(s.impossible.why), date: validDate(s.impossible.date) ? s.impossible.date : undefined, stay_id: s.impossible.stay_id != null ? String(s.impossible.stay_id) : undefined } : null,
    dismissed: s.kind === 'inquiry' && s.dismissed && typeof s.dismissed === 'object' ? { by: String(s.dismissed.by || ''), at: String(s.dismissed.at || '') } : null,
  }));
  d.blocks = arr<Block>(d.blocks).filter(b => b && b.listing_id != null && b.start && b.end).map(b => ({ ...b, listing_id: String(b.listing_id), note: typeof b.note === 'string' && b.note.trim() ? b.note : null, kind: b.kind === 'held' ? 'held' : 'blocked' }));
  d.rates = obj(d.rates); d.kpis = obj(d.kpis) as Kpis; d.filters = obj(d.filters) as FilterCounts;
  const ev = obj((j || {}).events);
  d.events = {
    turns: arr<any>(ev.turns).map(t => normTurn(t, d.as_of)).filter((t): t is TurnEv => !!t),
    work_orders: arr<WoEv>(ev.work_orders).filter(w => w && w.id != null && w.listing_id != null && validDate(w.date)).map(w => ({ ...w, id: String(w.id), listing_id: String(w.listing_id), title: String(w.title || 'Work order'), status: String(w.status || ''), priority: String(w.priority || 'routine'), kind: w.kind === 'pm' ? 'pm' : 'wo', who: w.who ? String(w.who) : null, end: validDate(w.end) && w.end > w.date ? w.end : null, due_date: validDate(w.due_date) ? w.due_date : null, due_only: w.due_only === true })),
  };
  return d;
}

export function normArrivalCheck(ac: any): ArrivalCheck | null {
  if (!ac || typeof ac !== 'object') return null;
  const items: CheckItem[] = (Array.isArray(ac.items) ? ac.items : []).filter((i: any) => i && i.key).map((i: any) => ({
    key: String(i.key), label: String(i.label || i.key), required: i.required === true || i.required === 'required',
    state: (ITEM_STATES as readonly string[]).includes(i.state) ? i.state as ItemState : 'pending',
    done_by: i.done_by ? String(i.done_by) : null, done_at: i.done_at ? String(i.done_at) : null, note: typeof i.note === 'string' && i.note.trim() ? i.note : null,
    due_at: i.due_at ? String(i.due_at) : null, overdue: i.overdue === true, photos: Array.isArray(i.photos) ? i.photos.length : Math.max(0, Number(i.photos) || 0),
    auto: i.auto && typeof i.auto === 'object' ? { src: String(i.auto.src || ''), at: i.auto.at ? String(i.auto.at) : null, evidence: i.auto.evidence ? String(i.auto.evidence) : null } : null,
  }));
  return {
    generated: ac.generated === true || (ac.generated !== false && items.length > 0), id: String(ac.id || ''), rollup: String(ac.rollup || (items.length ? 'pending' : 'not_generated')),
    required_done: Number(ac.required_done) || 0, required_total: Number(ac.required_total) || 0,
    hours_to_checkin: Number.isFinite(Number(ac.hours_to_checkin)) && ac.hours_to_checkin != null ? Number(ac.hours_to_checkin) : null,
    note: typeof ac.note === 'string' && ac.note.trim() ? ac.note : null, items, updated_at: ac.updated_at ? String(ac.updated_at) : null, eligible: ac.eligible === true,
  };
}

export function normEciLco(e: any, s: Stay): EciLco | null {
  if (!e || typeof e !== 'object') return null;
  const side = (v: any, kind: 'eci' | 'lco'): EciLcoSide | null => {
    if (!v || typeof v !== 'object') return null;
    return { on: v.on === true, src: v.src ? String(v.src) : null, time: typeof v.time === 'string' && v.time.trim() ? v.time.slice(0, 20) : null, by: v.by ? String(v.by) : null, at: v.at ? String(v.at) : null, declined: v.declined === true, key: String(v.key || `${s.listing_id}|${kind === 'eci' ? s.check_in : s.check_out}`), tight: v.tight === true };
  };
  return { eci: side(e.eci, 'eci'), lco: side(e.lco, 'lco') };
}

/** GET /api/stays/:id → the panel's Detail (every extra optional). */
export function normalizeDetail(j: any, today: string): Detail {
  const d = j as Detail;
  const s = d.stay;
  s.id = String(s.id); s.listing_id = String(s.listing_id);
  s.opportunities = Array.isArray(s.opportunities) ? s.opportunities : [];
  s.review = s.review && typeof s.review === 'object' ? s.review : null;
  s.checks = s.checks && typeof s.checks === 'object' && s.checks.rollup ? s.checks : null;
  s.negative = s.negative === true;
  d.guest = d.guest && typeof d.guest === 'object' ? d.guest : { name: s.guest_name, phone: null, email: null, guests: null, confirmation_code: null };
  d.details = d.details && typeof d.details === 'object' ? d.details : null;
  if (d.details && !(d.details.money && typeof d.details.money === 'object')) d.details.money = null;
  d.review = d.review && typeof d.review === 'object' && d.review.status ? d.review : s.review;
  const ev = d.events && typeof d.events === 'object' ? d.events : {};
  d.events = { turns: (Array.isArray(ev.turns) ? ev.turns : []).map(t => normTurn(t, today)).filter((t): t is TurnEv => !!t), work_orders: Array.isArray(ev.work_orders) ? ev.work_orders : [] };
  // R112 (both optional on an older server)
  s.impossible = s.kind === 'inquiry' && s.impossible && typeof s.impossible === 'object' && s.impossible.why ? s.impossible : null;
  s.dismissed = s.kind === 'inquiry' && s.dismissed && typeof s.dismissed === 'object' ? { by: String(s.dismissed.by || ''), at: String(s.dismissed.at || '') } : null;
  d.arrival_check = normArrivalCheck((j || {}).arrival_check);
  d.eci_lco = s.kind === 'inquiry' ? null : normEciLco((j || {}).eci_lco, s);
  return d;
}
